import React, { useState } from 'react';
import '../styles/donations.css';
import {
  getDonorFormInitialState,
  submitDonorApplication,
} from '../services/donationsService';
import { getPersonNameError, sanitizePersonNameInput } from '../utils/personName';

const donationTypeOptions = [
  { value: 'unica', label: 'Donación única' },
  { value: 'recurrente', label: 'Donación recurrente' },
];

const donationCategoryOptions = [
  { value: 'corporativa', label: 'Corporativa' },
  { value: 'monetaria', label: 'Monetaria' },
];

const DonationsPage = () => {
  const [formData, setFormData] = useState(getDonorFormInitialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formMessage, setFormMessage] = useState('');

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    const nextValue = name === 'nombreCompleto' ? sanitizePersonNameInput(value) : value;
    setFormData((current) => ({ ...current, [name]: nextValue }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setFormMessage('');

    const nameError = getPersonNameError(formData.nombreCompleto);
    if (nameError) {
      setFormMessage(nameError);
      return;
    }

    setIsSubmitting(true);

    submitDonorApplication(formData)
      .then(() => {
        setFormData(getDonorFormInitialState());
        setFormMessage('Tu solicitud de donación fue enviada correctamente. Gracias por tu apoyo.');
      })
      .catch((error) => {
        setFormMessage(error.message || 'No se pudo enviar la solicitud.');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className="donations-root">
      <section className="donations-section donations-section-intro">
        <div className="donations-block">
          <div className="donations-intro">
            <h1 className="donations-intro-title">Donaciones</h1>
            <p className="donations-intro-text">
              Con más de 12 años de trayectoria con Abrazando La Inclusión, la fundación Beyond Limits
              financia sus programas mediante donaciones que permiten desarrollar talleres, consultorías y
              actividades de impacto para niños, niñas y adolescentes neuro divergentes, así como la
              capacitación de docentes especializados.
            </p>
          </div>

          <div className="donations-grid">
            <div className="donations-grid-left">
              <article className="donations-info-card donations-info-card--blue">
                <p className="donations-info-text">
                  Las contribuciones <strong>económicas</strong> son esenciales para financiar nuestros
                  programas de apoyo comunitario y desarrollo sostenible a largo plazo.
                </p>
              </article>

              <article className="donations-info-card donations-info-card--yellow">
                <p className="donations-info-text">
                  Los <strong>aportes corporativos y de fundaciones</strong> permiten escalar proyectos
                  estratégicos, garantizando su sostenibilidad y maximizando el alcance de las intervenciones
                  sociales.
                </p>
              </article>
            </div>

            <article className="donations-feature-card">
              <div className="donations-feature-visual">
                <img
                  src={`${process.env.PUBLIC_URL}/images/donations-volunteers-photo.png`}
                  alt="Voluntarios empacando cajas de donación"
                  className="donations-feature-photo"
                />
              </div>
              <p className="donations-feature-text">
                A la fecha más de 100 personas se han acercado, conformando una comunidad que busca seguir
                participando activamente y aportando a la sociedad en este proyecto
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="donations-section donations-section-form">
        <div className="donations-form-card">
          <div className="donations-form-inner">
            <h2 className="donations-form-title">Formulario para donantes</h2>
            <p className="donations-form-intro">
              Tu donación transforma vidas. Con cada aporte, abrimos caminos hacia una nueva longevidad y
              empleabilidad significativa. ¿Nos ayudas a construir un futuro donde cada etapa de la vida esté
              llena de propósito y posibilidades?
            </p>

            <form className="donations-form" onSubmit={handleSubmit}>
              <div className="donations-form-field donations-form-field--full">
                <label className="donations-form-label" htmlFor="nombreCompleto">
                  Nombre completo
                </label>
                <input
                  id="nombreCompleto"
                  name="nombreCompleto"
                  type="text"
                  className="donations-form-input"
                  placeholder="Ingresa tu nombre completo"
                  value={formData.nombreCompleto}
                  onChange={handleFieldChange}
                  required
                />
              </div>

              <div className="donations-form-row">
                <div className="donations-form-field">
                  <label className="donations-form-label" htmlFor="email">
                    Correo electrónico
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    className="donations-form-input"
                    placeholder="Ingresa tu correo"
                    value={formData.email}
                    onChange={handleFieldChange}
                    required
                  />
                </div>

                <div className="donations-form-field">
                  <label className="donations-form-label" htmlFor="telefono">
                    Teléfono
                  </label>
                  <input
                    id="telefono"
                    name="telefono"
                    type="tel"
                    className="donations-form-input"
                    placeholder="Ingresa tu teléfono"
                    value={formData.telefono}
                    onChange={handleFieldChange}
                    required
                  />
                </div>
              </div>

              <div className="donations-form-row">
                <fieldset className="donations-form-field donations-form-fieldset">
                  <legend className="donations-form-label donations-form-label--legend">
                    Tipo de Donación
                    <span className="donations-form-chevron" aria-hidden="true">
                      ▾
                    </span>
                  </legend>
                  <div className="donations-form-options">
                    {donationTypeOptions.map((option) => (
                      <label key={option.value} className="donations-form-option">
                        <input
                          type="radio"
                          name="tipoDonacion"
                          value={option.value}
                          checked={formData.tipoDonacion === option.value}
                          onChange={handleFieldChange}
                        />
                        <span className="donations-form-option-box" aria-hidden="true" />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <fieldset className="donations-form-field donations-form-fieldset">
                  <legend className="donations-form-label donations-form-label--legend">
                    Donativo
                    <span className="donations-form-chevron" aria-hidden="true">
                      ▾
                    </span>
                  </legend>
                  <div className="donations-form-options">
                    {donationCategoryOptions.map((option) => (
                      <label key={option.value} className="donations-form-option">
                        <input
                          type="radio"
                          name="donativo"
                          value={option.value}
                          checked={formData.donativo === option.value}
                          onChange={handleFieldChange}
                        />
                        <span className="donations-form-option-box" aria-hidden="true" />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              </div>

              <div className="donations-form-field donations-form-field--full">
                <label className="donations-form-label" htmlFor="mensaje">
                  Dejar un mensaje
                </label>
                <textarea
                  id="mensaje"
                  name="mensaje"
                  className="donations-form-input donations-form-textarea"
                  placeholder="¿Deseas dedicar tu donación?"
                  rows={4}
                  value={formData.mensaje}
                  onChange={handleFieldChange}
                />
              </div>

              {formMessage ? <p className="donations-form-message">{formMessage}</p> : null}

              <button type="submit" className="donations-form-submit" disabled={isSubmitting}>
                {isSubmitting ? 'Enviando...' : 'Donar'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DonationsPage;
